import Lottie from 'lottie-react';
import React, { FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { isDefined } from 'ts-utils';
import { recordError } from '../../../util/Errors';

type MediaRendererProps = {
  src: string;
  altText?: string;
  maxWidth?: number;
  marginBottom?: boolean;
  isCard?: boolean;
  layered?: boolean;
  loop?: boolean;
};

/**
 * MediaRenderer Component
 *
 * Automatically detects the file type and renders appropriately:
 * - .json files → Lottie animation (fetches JSON data)
 * - Other files → Standard image
 *
 * Supports layered rendering for images with JSON overlay animations.
 */
export const MediaRenderer: FunctionComponent<MediaRendererProps> = ({
  src,
  altText = '',
  maxWidth,
  marginBottom,
  isCard,
  layered = false,
  loop = false,
}) => {
  // Check if running in Cypress
  const isCypressTest: boolean = typeof window !== 'undefined' && Boolean(window.Cypress);
  const isAnimation = src.endsWith('.json');
  const [animationData, setAnimationData] = useState<unknown | null>(null);
  const [overlayAnimationData, setOverlayAnimationData] = useState<unknown | null>(null);

  useEffect(
    function fetchAnimationData() {
      // Skip animation fetching in Cypress tests
      if (isCypressTest || !isAnimation) return;

      fetch(src)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          return response.json();
        })
        .then((data) => setAnimationData(data))
        .catch((error) => {
          recordError(
            new Error(`MediaRenderer: Failed to fetch animation from ${src}: ${error.message}`)
          );
          // User can continue - animation just won't show
        });
    },
    [isCypressTest, src, isAnimation]
  );

  // Fetch overlay animation for layered images
  useEffect(
    function fetchOverlayAnimationData() {
      // Skip animation fetching in Cypress tests
      if (isCypressTest || !layered || isAnimation) return;

      const overlayPath = src.replace(/\.(png|jpg|jpeg)$/, '.json');
      fetch(overlayPath)
        .then((response) => response.json())
        .then((data) => setOverlayAnimationData(data))
        .catch(() => {
          // Silently fail if overlay doesn't exist
        });
    },
    [isCypressTest, src, layered, isAnimation]
  );

  // In Cypress tests, skip animation rendering but still render regular images
  if (isCypressTest && isAnimation) {
    return <LottieWrapper maxWidth={maxWidth} marginBottom={marginBottom} isCard={isCard} />;
  }

  if (isAnimation && isDefined(animationData)) {
    return (
      <LottieWrapper maxWidth={maxWidth} marginBottom={marginBottom} isCard={isCard}>
        <Lottie
          animationData={animationData}
          loop={loop}
          autoplay={true}
          style={{ width: '100%', height: 'auto' }}
        />
      </LottieWrapper>
    );
  }

  // Render as regular image with optional layered animation
  const ImageComponent = isCard ? ImageShadowRounded : Image;

  // Layered rendering: static image with Lottie overlay
  if (layered && isDefined(overlayAnimationData)) {
    return (
      <LayeredContainer maxWidth={maxWidth} marginBottom={marginBottom}>
        <ImageComponent src={src} alt={altText} maxWidth={maxWidth} marginBottom={false} />
        <OverlayAnimation>
          <Lottie
            animationData={overlayAnimationData}
            loop={loop}
            autoplay={true}
            style={{ width: '100%', height: '100%' }}
          />
        </OverlayAnimation>
      </LayeredContainer>
    );
  }

  return <ImageComponent src={src} alt={altText} maxWidth={maxWidth} marginBottom={marginBottom} />;
};

// Styled components for images
const Image = styled.img<{ maxWidth?: number; marginBottom?: boolean }>`
  width: 100%;
  max-width: ${({ maxWidth }) => (isDefined(maxWidth) ? `${maxWidth}px` : '100%')};
  height: auto;
  margin: 0 auto;
  display: block;
  margin-bottom: ${({ marginBottom }) => (marginBottom ? '0' : '32px')};
`;

const ImageShadowRounded = styled(Image)`
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
`;

// Styled component for Lottie wrapper
const LottieWrapper = styled.div<{ maxWidth?: number; marginBottom?: boolean; isCard?: boolean }>`
  width: 100%;
  max-width: ${({ maxWidth }) => (isDefined(maxWidth) ? `${maxWidth}px` : '100%')};
  margin: 0 auto;
  display: block;
  margin-bottom: ${({ marginBottom }) => (marginBottom ? '0' : '32px')};
  ${({ isCard }) =>
    isCard &&
    `
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
    border-radius: 16px;
    overflow: hidden;
  `}
`;

// Styled components for layered rendering
const LayeredContainer = styled.div<{ maxWidth?: number; marginBottom?: boolean }>`
  position: relative;
  width: 100%;
  max-width: ${({ maxWidth }) => (isDefined(maxWidth) ? `${maxWidth}px` : '100%')};
  margin: 0 auto;
  display: block;
  margin-bottom: ${({ marginBottom }) => (marginBottom ? '0' : '32px')};
`;

const OverlayAnimation = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;
